import { Outlet, NavLink } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { Home, FolderArchive, Sparkles, MessagesSquare, ShoppingBasket, CalendarDays, Settings } from "lucide-react";
import { api } from "@/lib/api";
import { useSession } from "@/App";
import BrandLogo from "@/components/BrandLogo";
import MobileMoreMenu from "@/components/MobileMoreMenu";

const navItems = [
  { to: "/app", label: "Home", icon: Home, testid: "nav-home" },
  { to: "/vault", label: "Memoria", icon: FolderArchive, testid: "nav-vault" },
  { to: "/ai", label: "AI", icon: Sparkles, testid: "nav-ai" },
  { to: "/chat", label: "Chat", icon: MessagesSquare, testid: "nav-chat" },  { to: "/feed", label: "Memo AI", icon: Sparkles, testid: "nav-feed", showPendingBadge: true },
  { to: "/shopping", label: "Spesa", icon: ShoppingBasket, testid: "nav-shopping" },
  { to: "/calendar", label: "Agenda", icon: CalendarDays, testid: "nav-calendar" },
];

function usePendingSuggestions() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/memo/suggestions", { params: { status_filter: "pending" } });
        if (!cancelled) setCount(Array.isArray(data) ? data.length : 0);
      } catch (err) {
        // Non-fatal: polling will retry. Log at debug level for diagnostics.
        if (process.env.NODE_ENV !== "production") {
          console.debug("Pending suggestions poll failed:", err?.response?.status || err?.message);
        }
      }
    };
    fetchCount();
    const id = setInterval(fetchCount, 8000);
    // Refresh when window regains focus (after approving from another tab/route)
    const onFocus = () => fetchCount();
    const onChange = () => fetchCount();
    window.addEventListener("focus", onFocus);
    window.addEventListener("memo:suggestion-changed", onChange);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("memo:suggestion-changed", onChange);
    };
  }, []);
  return count;
}

function Badge({ count, testid }) {
  if (!count) return null;
  return (
    <span
      data-testid={testid}
      className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-brand text-white text-[10px] font-semibold grid place-items-center leading-none"
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

export default function AppLayout() {
  const { user, family } = useSession();
  const pendingCount = usePendingSuggestions();
  // navItems is a module-level constant but filter/slice produce a new array on every
  // render. useMemo avoids that allocation churn for the bottom-nav.
  const mobileNavItems = useMemo(
    () => navItems.filter((n) => n.to !== "/feed").slice(0, 6),
    []
  );

  return (
    <div className="min-h-screen bg-app text-primary-fg flex flex-col md:flex-row">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-60 lg:w-64 shrink-0 flex-col border-r border-subtle p-6 sticky top-0 h-screen">
        <div className="flex items-center gap-3 mb-10">
          <BrandLogo size={40} />
          <div>
            <div className="font-display font-semibold text-lg leading-none">Family Brain</div>
            <div className="text-xs text-tertiary-fg mt-0.5 uppercase tracking-widest">Famiglia</div>
          </div>
        </div>

        <nav className="flex flex-col gap-1">
          {navItems.map(({ to, label, icon: Icon, testid, showPendingBadge }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/app"}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors text-sm ${
                  isActive ? "bg-elevated text-primary-fg" : "text-secondary-fg hover:bg-elevated hover:text-primary-fg"
                }`
              }
            >
              <Icon className="w-4 h-4" strokeWidth={1.6} />
              <span>{label}</span>
              {showPendingBadge && <Badge count={pendingCount} testid="nav-feed-pending-badge" />}
            </NavLink>
          ))}
          <NavLink
            to="/settings"
            data-testid="nav-settings"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors text-sm mt-6 ${
                isActive ? "bg-elevated text-primary-fg" : "text-secondary-fg hover:bg-elevated hover:text-primary-fg"
              }`
            }
          >
            <Settings className="w-4 h-4" strokeWidth={1.6} />
            <span>Impostazioni</span>
          </NavLink>
        </nav>

        <div className="mt-auto pt-6 border-t border-subtle">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-full grid place-items-center text-white text-sm font-semibold"
              style={{ backgroundColor: user?.avatar_color || "#E07A5F" }}
            >
              {user?.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs text-tertiary-fg truncate">{family?.name}</div>
            </div>
          </div>
        </div>
      </aside>

      <main className="flex-1 pb-24 md:pb-0 min-h-screen">
        <Outlet />
      </main>

      {/* Mobile-only floating "more" button that opens Impostazioni, Logout, etc. */}
      <MobileMoreMenu />

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 glass-panel border-t border-subtle px-2 py-2 flex justify-around items-center" data-testid="bottom-nav">
        {mobileNavItems.map(({ to, label, icon: Icon, testid }) => {
          // surface Memo AI pending count on the Chat tab so mobile users notice it
          const isChat = to === "/chat";
          return (
            <NavLink
              key={to}
              to={to}
              end={to === "/app"}
              data-testid={`${testid}-mobile`}
              className={({ isActive }) =>
                `relative flex flex-col items-center gap-1 px-2 py-1.5 rounded-xl text-[10px] transition-colors ${
                  isActive ? "text-brand" : "text-tertiary-fg"
                }`
              }
            >
              <div className="relative">
                <Icon className="w-5 h-5" strokeWidth={1.6} />
                {isChat && pendingCount > 0 && (
                  <span
                    data-testid="nav-chat-pending-dot"
                    className="absolute -top-1 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-brand text-white text-[9px] font-semibold grid place-items-center leading-none border border-app"
                  >
                    {pendingCount > 9 ? "9+" : pendingCount}
                  </span>
                )}
              </div>
              <span>{label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
